import type { Absence } from '@src/features/absence';
import {
  formatIsoDate,
  isIsoDateRangeOrdered,
  isSameDate,
  parseIsoDate,
} from '@src/shared/lib';
import type { SelectedAbsence, TimelineSlot, VisibleRange } from '../types';
import { getUserAbsences } from './absence';
import { absenceGroupOf } from './blocks';

const FIXED_MONTH_DAY_COUNT = 31;

export function getFixedMonthVisibleRange(
  anchorDate: Date,
  dayCount: number = FIXED_MONTH_DAY_COUNT,
): VisibleRange {
  const start = new Date(
    anchorDate.getFullYear(),
    anchorDate.getMonth(),
    anchorDate.getDate(),
  );
  const end = new Date(start);
  end.setDate(end.getDate() + dayCount);

  return { start, end };
}

/**
 * Where the window goes, and how much of it there has to be.
 *
 * A month is the width the calendar opens on, not a limit: a run longer than
 * that gets a window long enough to hold it and the timeline scrolls. Showing
 * part of a run and letting somebody decide on it is the thing worth avoiding
 * -- what they approve would not be what they were looking at.
 */
export type FocusWindow = { start: Date; days: number };

/**
 * Whether the run behind a selection reaches past the days on screen.
 *
 * A selection carries its block's whole membership, however far past the
 * window that reaches -- the projection sends every member of every block it
 * names -- so both ends here are the run's real ones and this is a question
 * about what is *shown*, not about what was fetched.
 *
 * It is worth asking because a run only partly on screen is a run somebody can
 * decide on without having seen it: approving what is drawn would approve
 * those days and quietly leave the rest of the absence undecided.
 */
export function isSelectionClipped(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): boolean {
  return clippedSides(selection, visibleRange) !== null;
}

function clippedSides(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): 'start' | 'end' | 'both' | null {
  if (!selection.start || !selection.end) {
    return null;
  }
  const start = new Date(`${selection.start}T00:00:00`);
  const end = new Date(`${selection.end}T00:00:00`);
  const before = start < visibleRange.start;
  const after = end >= visibleRange.end;

  if (before && after) return 'both';
  if (before) return 'start';
  if (after) return 'end';
  return null;
}

/**
 * Where to put the window so a clipped selection is shown whole.
 *
 * Normally a week before the absence, the same run-up a deep link opens with,
 * so it is read in the context of the days around it rather than starting
 * flush against the edge.
 *
 * The window is a fixed 31 days, so a long run cannot afford the week. Then it
 * takes the whole window, pinned to whichever end is still hidden: a run
 * overhanging the right edge gets pinned to its first day, one overhanging the
 * left gets its last known day put at the far end. Either way the days that
 * were missing come back in one move rather than one day at a time.
 *
 * The window keeps its usual width here, unlike a run named by a task
 * reference. A selection built from loaded rows has been cut off at whichever
 * edge it overran, so its own end says nothing about how far the run really
 * goes -- sizing a window to it would size it to the truncation. One window at
 * a time, and the caller asks again once the days it revealed have arrived.
 *
 * Returns null when nothing needs to move, or when the run is longer than the
 * window can hold at all and moving would only trade one clipped end for the
 * other.
 */
export function getFocusWindowStart(
  selection: SelectedAbsence,
  visibleRange: VisibleRange,
): Date | null {
  const sides = clippedSides(selection, visibleRange);
  if (sides === null || sides === 'both') {
    return null;
  }

  const first = new Date(`${selection.start}T00:00:00`);
  const last = new Date(`${selection.end}T00:00:00`);
  if (daysCovered(first, last) > FIXED_MONTH_DAY_COUNT) {
    return null;
  }

  let anchor = addDays(first, -LEAD_IN_DAYS);
  if (daysCovered(anchor, last) > FIXED_MONTH_DAY_COUNT) {
    anchor =
      sides === 'start' ? addDays(last, -(FIXED_MONTH_DAY_COUNT - 1)) : first;
  }

  const start = new Date(
    anchor.getFullYear(),
    anchor.getMonth(),
    anchor.getDate(),
  );
  return start.getTime() === visibleRange.start.getTime() ? null : start;
}

/**
 * The window that holds ``first`` through ``last``, or null if one already is.
 *
 * A run that fits the usual month keeps the usual week of run-up. One that
 * does not gets exactly the width it needs, starting where it starts: there is
 * no run-up to spare, and the days that matter are the ones in the run.
 *
 * Only safe where both ends are known for certain -- which means a task
 * reference, since it carries them.
 */
function windowHolding(
  first: Date,
  last: Date,
  visibleRange: VisibleRange,
): FocusWindow | null {
  // `end` is exclusive, so the last day drawn is the day before it.
  const drawnThrough = addDays(visibleRange.end, -1);
  if (first >= visibleRange.start && last <= drawnThrough) {
    // Already all on screen. Worth checking before anything else: every range
    // change rebuilds the grid, and the rebuild is what outruns ScrollGrid's
    // layout budget.
    return null;
  }

  const runLength = daysCovered(first, last);
  const fitsWithRunUp = runLength + LEAD_IN_DAYS <= FIXED_MONTH_DAY_COUNT;
  const anchor = fitsWithRunUp ? addDays(first, -LEAD_IN_DAYS) : first;
  const days = fitsWithRunUp ? FIXED_MONTH_DAY_COUNT : runLength;

  return {
    start: new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()),
    days,
  };
}

/**
 * Where to put the window so every day a task reference names is loaded.
 *
 * Worked out from the reference's own first and last day rather than from what
 * resolved, which is the whole point: a run reaching past the window resolves
 * to the days inside it and looks complete, so a selection can never be
 * trusted to say how far it was meant to reach.
 *
 * A run longer than the month the calendar opens on gets a window as long as
 * it needs to be; the timeline scrolls. Showing part of a run would let
 * somebody approve days they never saw.
 *
 * Returns null when the window already holds it, which is the signal that
 * everything the reference names has been asked for.
 */
export function getReferenceWindowStart(
  firstIsoDate: string,
  lastIsoDate: string,
  visibleRange: VisibleRange,
): FocusWindow | null {
  const first = new Date(`${firstIsoDate}T00:00:00`);
  const last = new Date(`${lastIsoDate}T00:00:00`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) {
    return null;
  }
  return windowHolding(first, last, visibleRange);
}

/** How many days a window has to span to hold both ends inclusively. */
function daysCovered(first: Date, last: Date): number {
  const day = 24 * 60 * 60 * 1000;
  return Math.round((last.getTime() - first.getTime()) / day) + 1;
}

function addDays(value: Date, days: number): Date {
  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

/**
 * Days of run-up left before an absence the calendar is being opened on.
 *
 * A week, so the absence is read in the context of the days around it rather
 * than starting flush against the edge. The remaining three weeks of the
 * window are room for it to run into; anything longer gives up the run-up
 * rather than the absence.
 */
const LEAD_IN_DAYS = 7;

/**
 * The 31-day window the calendar opens on.
 *
 * `anchorIsoDate` lets a deep link (a task reference naming an absence in
 * another month) seed the range up front, so the calendar mounts where it is
 * wanted instead of jumping there imperatively a frame later.
 *
 * With an anchor the window opens just before it rather than on the first of
 * its month, which is what keeps a deep link to a single range change. A month
 * is a poor window for something that does not respect months: an absence
 * starting on the 28th has three days of it left to run into and spills out,
 * and moving the window afterwards to take in the rest costs a second grid
 * rebuild -- the one that outruns ScrollGrid's layout budget and collapses the
 * lanes for a moment. Opening a few days early leaves nearly the whole window
 * ahead of it, so there is usually nothing to correct.
 */
/**
 * Whether the window on screen contains today.
 *
 * Asked so the Today button can say whether pressing it would move anything.
 * The day view used to compare `getDay()` -- the weekday *index* -- which lit
 * the button on any Monday while showing some other Monday, and left it dark on
 * today itself whenever the two weekdays differed.
 *
 * `now` is a parameter so the answer is testable without moving the clock.
 */
export function windowShowsToday(
  window: { start: Date; end: Date },
  { dayView, now = new Date() }: { dayView: boolean; now?: Date },
): boolean {
  // The day view shows one day, so the window's own start is the question. The
  // month view spans a range, and `end` is exclusive.
  return dayView
    ? isSameDate(window.start, now)
    : now >= window.start && now < window.end;
}

export function getInitialVisibleRange(
  anchorIsoDate?: string | null,
): VisibleRange {
  const anchor = anchorIsoDate
    ? new Date(`${anchorIsoDate}T00:00:00`)
    : new Date();

  if (anchorIsoDate && !Number.isNaN(anchor.getTime())) {
    return getFixedMonthVisibleRange(addDays(anchor, -LEAD_IN_DAYS));
  }

  const fallback = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  return getFixedMonthVisibleRange(
    new Date(fallback.getFullYear(), fallback.getMonth(), 1),
  );
}

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<Absence>} absences
 *        List of absence objects to search through.
 * @param {boolean} single
 *        Gets data for single absenece object with specified id or
 *        for all absences if it is multiday absence event
 */
export function getAbsenceSelectionById(
  absenceId: number,
  absences: Absence[],
  single = false,
): SelectedAbsence | null {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (single || !anchor.allDay) {
    return hydrateSelectedAbsence(
      {
        userId: anchor.userId,
        start: formatIsoDate(anchor.date),
        end: formatIsoDate(anchor.date),
        startTime: anchor.startTime,
        endTime: anchor.endTime,
        absenceIds: [anchor.id],
      },
      absences,
    );
  }

  const group = absenceGroupOf(anchor, absences);
  if (!group.length) {
    return null;
  }

  return hydrateSelectedAbsence(
    {
      userId: group[0].userId,
      start: formatIsoDate(group[0].date),
      end: formatIsoDate(group[group.length - 1].date),
      startTime: null,
      endTime: null,
      absenceIds: group.map((a) => a.id),
    },
    absences,
  );
}

/**
 * The run of days a withdrawal request covers, around `anchor`.
 *
 * The server flags every day it named, not just the one the task points at, so
 * the run is read off the flags rather than guessed at from the block. It can
 * be a slice out of the middle of a longer entry -- days being given back
 * because a change is replacing them, say -- which is why the walk stops at the
 * first unflagged day either side.
 */
/**
 * Selection for the days a task reference names.
 *
 * The reference carries them, so they are taken as given rather than worked
 * out from the calendar: a withdrawal request can name a slice out of the
 * middle of an entry, and by the time anyone follows the link the entry may
 * have been cut about by another decision. Days that have since gone are
 * dropped, and a reference naming none of what is loaded selects nothing.
 */
export function getAbsenceReferenceSelection(
  absenceIds: number[],
  absences: Absence[],
): SelectedAbsence | null {
  const named = absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => absence !== undefined)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const [first] = named;
  if (!first) {
    return null;
  }

  // A part-day absence stands alone and keeps its times; a run of whole days
  // is one bar from the first to the last.
  if (named.length === 1 && !first.allDay) {
    return hydrateSelectedAbsence(
      {
        userId: first.userId,
        start: formatIsoDate(first.date),
        end: formatIsoDate(first.date),
        startTime: first.startTime,
        endTime: first.endTime,
        absenceIds: [first.id],
      },
      absences,
    );
  }

  const last = named[named.length - 1];
  return hydrateSelectedAbsence(
    {
      userId: first.userId,
      start: formatIsoDate(first.date),
      end: formatIsoDate(last.date),
      startTime: null,
      endTime: null,
      absenceIds: named.map((absence) => absence.id),
    },
    absences,
  );
}

// Local-time construction on purpose: `new Date('YYYY-MM-DD')` parses as UTC
// midnight, which formats as the previous day anywhere west of UTC. Not
// exported: the shared formatters read a day string as its own day, so nothing
// outside this file has to convert one first.
function localDateFromIso(day: string): Date {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  return new Date(year, month - 1, dayOfMonth);
}
function isoDayAfter(day: string): string {
  const date = localDateFromIso(day);
  date.setDate(date.getDate() + 1);
  return formatIsoDate(date);
}
export function isoDaysBetween(from: Date, to: Date): string[] {
  const days: string[] = [];
  let day = formatIsoDate(from);
  const last = formatIsoDate(to);
  while (day <= last) {
    days.push(day);
    day = isoDayAfter(day);
  }
  return days;
}
export function isoRuns(days: string[]): Array<[string, string]> {
  const runs: Array<[string, string]> = [];
  [...days].sort().forEach((day) => {
    const lastRun = runs[runs.length - 1];
    if (lastRun && isoDayAfter(lastRun[1]) === day) {
      lastRun[1] = day;
    } else {
      runs.push([day, day]);
    }
  });
  return runs;
}

/**
 * Whether the days asked for are not the days the entry already has.
 *
 * Every day, not the two ends. What is being edited can be more than one run
 * -- a request reaching two entries of a type reshapes both -- and then the
 * ends can agree while the days between them do not: asking for 20th to 30th
 * over runs of 20th-24th and 28th-30th starts and finishes exactly where
 * they do and still adds three days in the middle.
 */
export function hasRangeChanged(
  entry: Absence[],
  from: Date,
  to: Date,
): boolean {
  if (!entry.length) return false;
  const covered = new Set(entry.map((absence) => absence.dateStr));
  const requested = isoDaysBetween(from, to);
  return (
    requested.length !== covered.size ||
    requested.some((day) => !covered.has(day))
  );
}

/**
 * Whether a submit leaves the entry it names exactly as it is.
 *
 * The days it already has, of the type and tentativeness it already has:
 * there is nothing for anyone to decide, so the server writes the notes and
 * asks nobody -- see ``_keep_as_it_is``. Telling the user an approver has to
 * agree to it would describe a change that is not going to happen.
 */
export function leavesEntryUnchanged(
  entry: Absence[],
  typeId: unknown,
  tentative: boolean,
  from: Date,
  to: Date,
): boolean {
  if (!entry.length) return false;
  if (
    entry.some(
      (absence) =>
        absence.typeId !== typeId || absence.absenceTentative !== tentative,
    )
  ) {
    return false;
  }
  return !hasRangeChanged(entry, from, to);
}

/**
 * An entry's notes, off the day that holds them.
 *
 * The first day carrying any rather than simply the first day: a change that
 * also adds days in front of the absence it replaces keeps its notes on the
 * first *replaced* day, because an added day is deleted when refused and the
 * notes would go with it. Reading the head regardless showed the entry as
 * having none, and saving the edit form then cleared them. Mirrors
 * ``notes_holder`` on the server, which writes them back to the same day.
 */
export function entryNotes(
  entry: readonly Absence[],
  field: 'notes' | 'notesHr',
): string | undefined {
  return (entry.find((absence) => absence[field]) ?? entry[0])?.[field];
}

export function hydrateSelectedAbsence(
  selection: SelectedAbsence,
  absences: Absence[],
): SelectedAbsence {
  if (!selection.absenceIds.length || !absences.length) {
    return selection;
  }

  const snapshotAbsences = selection.absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (!snapshotAbsences.length) {
    return selection;
  }

  const primaryAbsenceId =
    selection.primaryAbsenceId ?? selection.absenceIds[0];
  const primaryAbsence =
    snapshotAbsences.find((absence) => absence.id === primaryAbsenceId) ??
    snapshotAbsences[0];
  const entry = primaryAbsence.allDay
    ? absenceGroupOf(primaryAbsence, absences)
    : [primaryAbsence];

  return {
    ...selection,
    primaryAbsenceId: primaryAbsence.id,
    typeId: primaryAbsence.typeId ?? null,
    absenceTentative: primaryAbsence.absenceTentative ?? false,
    approvedManager: primaryAbsence.approvedManager ?? null,
    approvedHr: primaryAbsence.approvedHr ?? null,
    notes: entryNotes(entry, 'notes') ?? primaryAbsence.notes ?? '',
    notesHr: entryNotes(entry, 'notesHr') ?? primaryAbsence.notesHr ?? '',
    snapshotAbsences,
  };
}

/*
   Returns an array of free dates  within the slot range that are
   not occupied by absences.
*/
export function getSlotFreeDates(
  slot: TimelineSlot,
  absences: Absence[],
): Date[] {
  if (!slot?.start || !slot.end) {
    return [];
  }

  const startDate = parseIsoDate(slot.start);
  const endDate = parseIsoDate(slot.end);
  if (!startDate || !endDate || startDate > endDate) {
    return [];
  }
  const start = slot.start;
  const end = slot.end;

  const occupied = new Set(
    (absences || [])
      .filter((a) => !(a.dateStr < start) && !(a.dateStr > end))
      .map((a) => a.dateStr),
  );
  const free = [];
  let cur = startDate;

  while (cur <= endDate) {
    if (!occupied.has(formatIsoDate(cur))) {
      free.push(cur);
    }
    const nextDate = cur.getDate() + 1;
    cur = new Date(cur);
    cur.setDate(nextDate);
  }
  return free;
}

/**
 * Absences the calendar should paint as the active selection.
 *
 * Editing reshapes one row, so the edited absence stays lit wherever it moves.
 * Withdrawing is the opposite: the edited ids only bound how far the withdrawal
 * may reach, so the live range in `selectedIds` is what should light up -- the
 * day that happened to be clicked must not stay selected once the range moves
 * off it.
 */
export function getActiveAbsenceIds(slot: TimelineSlot | null): number[] {
  if (!slot) return [];
  if (slot.withdrawing) return slot.selectedIds;
  return slot.editedAbsenceId ? [slot.editedAbsenceId] : slot.selectedIds;
}

export function isAbsenceSelected(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }
  if (!isIsoDateRangeOrdered(slot.start, slot.end)) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const selectedIds = [...slot.selectedIds].sort((a, b) => a - b);

  const selectedAbsences = selectedIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (selectedAbsences.length !== selectedIds.length) {
    return false;
  }

  const [firstAbsence] = selectedAbsences;
  if (!firstAbsence) {
    return false;
  }

  const groupedSelection = getAbsenceSelectionById(firstAbsence.id, absences);
  const singleSelection = getAbsenceSelectionById(
    firstAbsence.id,
    absences,
    true,
  );

  const isWithinGroupedSelection = groupedSelection
    ? slot.userId === groupedSelection.userId &&
      slot.start >= groupedSelection.start &&
      slot.end <= groupedSelection.end
    : false;

  const allSameType = selectedAbsences.every(
    (absence) =>
      firstAbsence.typeId != null &&
      absence.userId === firstAbsence.userId &&
      absence.typeId === firstAbsence.typeId &&
      absence.absenceTentative === firstAbsence.absenceTentative &&
      absence.approvedManager === firstAbsence.approvedManager &&
      absence.approvedHr === firstAbsence.approvedHr &&
      absence.allDay === firstAbsence.allDay,
  );

  const matchesTimes = selectedAbsences.every(
    (absence) =>
      absence.startTime === firstAbsence.startTime &&
      absence.endTime === firstAbsence.endTime,
  );

  const matchesSingleSelection =
    !!singleSelection &&
    slot.start === singleSelection.start &&
    slot.end === singleSelection.end &&
    slot.startTime === singleSelection.startTime &&
    slot.endTime === singleSelection.endTime;

  return (
    isWithinGroupedSelection &&
    allSameType &&
    (matchesTimes || matchesSingleSelection)
  );
}

export function isFullyOccupiedSlot(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }
  if (!isIsoDateRangeOrdered(slot.start, slot.end)) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const isSingleDay = slot.start === slot.end;
  if (isSingleDay) {
    return true;
  }

  const selectedAbsences = slot.selectedIds
    .map((id) => absences.find((a) => a.id === id))
    .filter((a): a is Absence => !!a);
  const first = selectedAbsences[0];

  return (
    !!first &&
    selectedAbsences.every(
      (a) =>
        a.typeId === first.typeId &&
        a.userId === first.userId &&
        a.absenceTentative === first.absenceTentative &&
        a.approvedManager === first.approvedManager &&
        a.approvedHr === first.approvedHr &&
        a.allDay === first.allDay,
    )
  );
}
